import React, {useState} from 'react';
import {practitionersData} from "../../data/practitioners/practitionersData";
import {Card, Col, Collapse, Fade, ListGroup, ListGroupItem, Row} from "react-bootstrap";
import Identifiers from "../../components/identifiers/Identifiers";
import {useLoaderData} from "react-router-dom";
import OrganizationInfo from "./OrganizationInfo";
import Addresses from "../../components/addresses/Addresses";
import OrganizationContacts from "./contacts/OrganizationContacts";
import ContactPoints from "../../components/contactpoints/ContactPoints";
import OrganizationLog from "./OrganizationLog";
import RelatedOrganizations from "./RelatedOrganizations";
import DetailsPageHeader from "../../components/common/headers/DetailsPageHeader";
import {usePreloadedQuery} from "react-relay";
import graphql from "babel-plugin-relay/macro";
import {useUser} from "../../hooks/useUser";
import ContractsTable from "../contracts/ContractsTable";
import {DateCell, LinkCell} from "../../components/common/advance-table/CustomCells";
import DeletePanel from "../../components/common/repeated-page-panels/DeletePanel";
import OrganizationIdentifiers from "./identifiers/OrganizationIdentifiers";
import OrganizationContactPoints from "./contactpoints/OrganizationContactPoints";
import OrganizationAddresses from "./addresses/OrganizationAddresses";
import Attachments from "../../components/attachments/Attachments";
import Notes from "../../components/notes/Notes";
import Flex from "../../components/common/Flex";
import OrganizationContracts from "./OrganizationContracts";

const fakeOrgData = {
    name: "Conway Regional",
    practitioners: practitionersData.filter(p => p.organization === 'Conway Regional'),
    parentOrganization: {
        name: "<<NONE>>"
    },
    contracts: [
        {
            id: 1,
            effectiveFrom: '2/5/2019',
            effectiveTo: '<current>',
            network: 'Vantos',
            linesOfService: 'Arkansas Medicare, Arkansas Medicaid',
            contractedOrganization: 'Conway Regional'
        }
    ],
    relatedOrganizations: [
        {
            id: 1,
            name: 'Conway Regional System',
            effectiveFrom: '2/5/2019',
            effectiveTo: '<current>',
            relationship: 'Parent Company'
        }
    ]
}

const contractColumns = [
    {
        accessor: 'name',
        Header: 'Name',
        headerProps: { className: 'pe-1' },
        cellProps: {
            className: 'py-2'
        },
        Cell: rowData => {
            const { network } = rowData.row.original;
            return <LinkCell name={network.name} to={"/networks/" + network.id} />
        }
    },
    {
        accessor: 'startDate',
        Header: 'Start Date',
        Cell: DateCell
    },
    {
        accessor: 'endDate',
        Header: 'End Date',
        Cell: DateCell
    }
]
const OrganizationDetails = () => {
    const loader = useLoaderData();
    const data = usePreloadedQuery(
        graphql`query OrganizationDetailsQuery($id: ID!) {
                node(id: $id) {
                    ... on Organization {
                        id
                        primaryName
                        ...OrganizationInfo_Organization
                        contacts {
                            ...OrganizationContacts_Contacts
                        }
                        identifiers {
                            ...OrganizationIdentifiers_Identifiers
                        }
                        contracts {
                            ...OrganizationContracts_Contracts
                        }
                        contactPoints {
                            ...OrganizationContactPoints_ContactPoints
                        }
                        addresses {
                            ...OrganizationAddresses_Addresses
                        }
                        attachments {
                            ...Attachments
                        }
                        notes {
                            ...Notes
                        }
                    }
                }
                allNetworkOwners {
                    ...NetworkOwnersFragments_AllNetworkOwnersDropdown
                }
                allIdentifierTypes {
                    ...Fragments_AllIdentifierTypes
                }
                allIdentifierUseCodes {
                ...Fragments_AllIdentifierUseCodes
                }
                allContactPointSystemCodes {
                    ...Fragments_AllContactPointSystemCodesDropdown
                }
                allContactPointUseCodes {
                    ...Fragments_AllContactPointUseCodesDropdown
                }
                allAddressUseCodes {
                    ...Fragments_AllAddressUseCodesDropdown
                }
            allAttachmentCategories {
                ...Fragments_AllAttachmentCategoriesDropdown
            }
            }
        `, loader.organizationDetails)
    const deleteMutation = graphql`
        mutation OrganizationDetails_DeleteMutation($id: ID!) {
            deleteNode(input: {id: $id}) {
                deletedNodeId @deleteRecord
            }
        }
    `
    const {hasPermission} = useUser();
    return (
        <>
            <DetailsPageHeader iconType={"building"} iconText={"Contracting Entity"} title={data.node.primaryName} />
            <Row className={"mb-3"}>
                <Col md={5}>
                    <OrganizationInfo organization={data.node} networkOwners={data.allNetworkOwners}/>
                </Col>
                {hasPermission("OrganizationNote.Read") && <Col>
                        <Notes parentId={data.node.id}
                               notes={data.node.notes}
                               createPermission={"OrganizationNote.Create"}
                               editPermission={"OrganizationNote.Update"}
                               deletePermission={"OrganizationNote.Delete"}
                               paginationSize={3}
                        />
                    </Col>
                }
            </Row>
            <Row>
                {hasPermission("Contract.Read") &&
                <Col>
                    <OrganizationContracts contracts={data.node.contracts} />
                </Col>
                }
            </Row>
            <Row>
                {hasPermission("OrganizationAddress.Read") &&
                    <Col>
                        <OrganizationAddresses organizationId={data.node.id} 
                                               addresses={data.node.addresses} 
                                               addressUseCodes={data.allAddressUseCodes}
                        />
                    </Col>
                }
            </Row>
            <Row>
                {hasPermission("OrganizationContactPoint.Read") &&
                    <Col md={6}>
                        <OrganizationContactPoints contactPoints={data.node.contactPoints} 
                                                   organizationId={data.node.id} 
                                                   contactPointSystemCodes={data.allContactPointSystemCodes} 
                                                   contactPointUseCodes={data.allContactPointUseCodes}
                        />
                    </Col>
                }
                {hasPermission("OrganizationContact.Read") &&
                    <Col md={6}>
                        <OrganizationContacts contacts={data.node.contacts} organizationId={data.node.id} />
                    </Col>
                }
            </Row>
            {hasPermission("OrganizationAttachment.Read") && 
            <Row>
                <Col>
                    <Attachments attachments={data.node.attachments} 
                                 deletePermission={"OrganizationAttachment.Delete"}
                                 createPermission={"OrganizationAttachment.Create"}
                                 attachmentCategories={data.allAttachmentCategories}
                                 parentId={data.node.id}
                    />
                </Col>
            </Row>}
            {hasPermission("Organization.Delete") && 
                <DeletePanel mutation={deleteMutation} 
                             redirectUrl={"/organizations"} 
                             thingToDelete={"Contracting Entity"} id={data.node.id}>
                    In order to delete a Contracting Entity, there must be no related data
                </DeletePanel>}
        </>
    );
};

export default OrganizationDetails;
